import { Box, List, SliderField } from '@iheartjane/reefer';
import type { MinMax } from '@iheartjane/types';
import isEqual from 'lodash-es/isEqual';
import { useCallback, useState } from 'react';

import {
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import { FilterCategoryLabel } from '@jane/bloom/hooks/filters/useProductSearchFilters';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';

import { FilterActions } from './filterActions';

function parseRange(
  range: MinMax,
  value?: Partial<MinMax>,
  defaultRange?: boolean
) {
  if (defaultRange || (!value?.max && !value?.min))
    return { max: range.max, min: range.min };

  return {
    max: value.max || range.max,
    min: value.min || range.min,
  };
}

interface FilterPotenciesButtonProps {
  availableProductsCount?: number;
  /** Injected by FilterButtonWrapper */
  closePopover?: () => void;
}

export function FilterPotenciesButton({
  availableProductsCount,
  closePopover,
}: FilterPotenciesButtonProps) {
  const { clearFilter, clearFilters, getFilterRangeValue, setFilterRange } =
    useSearchParamFilters('cbdPotencies', 'thcPotencies');
  const { getPotencyFilterKeysNonEmpty, getProductSearchFilterOptions } =
    useProductSearchFilters();

  const validPotencySliderKeys = getPotencyFilterKeysNonEmpty().filter(
    (key) => {
      const filterList = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterList);
      return rangeMax > rangeMin;
    }
  );

  const getFilterOptionsRange = useCallback(
    (key: 'cbdPotencies' | 'thcPotencies') => {
      const filterOptions = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterOptions);
      return { max: rangeMax, min: rangeMin };
    },
    [getProductSearchFilterOptions]
  );

  const isRangeFilterAtDefault = useCallback(
    (key: 'cbdPotencies' | 'thcPotencies', val: MinMax) => {
      const range = getFilterOptionsRange(key);
      return val.max === range.max && val.min === range.min;
    },
    [getFilterOptionsRange]
  );

  const [cbdDisplayVals, changeCbdDisplayVals] = useState(
    parseRange(
      getFilterOptionsRange('cbdPotencies'),
      getFilterRangeValue('cbdPotencies')
    )
  );

  const [thcDisplayVals, changeThcDisplayVals] = useState(
    parseRange(
      getFilterOptionsRange('thcPotencies'),
      getFilterRangeValue('thcPotencies')
    )
  );

  const cbdValidValues =
    getProductSearchFilterOptions('cbdPotencies').filter(
      (filter) => filter.count > 0
    ).length > 1;

  const thcValidValues =
    getProductSearchFilterOptions('thcPotencies').filter(
      (filter) => filter.count > 0
    ).length > 1;

  const resetAllPotencies = () => {
    changeCbdDisplayVals(
      parseRange(
        getFilterOptionsRange('cbdPotencies'),
        getFilterRangeValue('cbdPotencies'),
        true
      )
    );
    changeThcDisplayVals(
      parseRange(
        getFilterOptionsRange('thcPotencies'),
        getFilterRangeValue('thcPotencies'),
        true
      )
    );
    clearFilters();
  };

  const onAfterSliderChange =
    (filterKey: 'cbdPotencies' | 'thcPotencies') => (val: MinMax) => {
      if (isRangeFilterAtDefault(filterKey, val)) {
        clearFilter(filterKey);
      } else {
        const filterableMin = 0.000001;
        const isDefaultRange = isEqual(
          getFilterOptionsRange(filterKey),
          filterKey === 'cbdPotencies' ? cbdDisplayVals : thcDisplayVals
        );

        const shouldUpdateMin =
          !isDefaultRange &&
          (filterKey === 'cbdPotencies'
            ? cbdDisplayVals.min
            : thcDisplayVals.min) === 0;

        if (shouldUpdateMin) {
          setFilterRange(filterKey, {
            max:
              filterKey === 'cbdPotencies'
                ? cbdDisplayVals.max
                : thcDisplayVals.max,
            min: filterableMin,
          });
        } else {
          setFilterRange(filterKey, val);
        }
      }
    };

  return (
    <Box px={16} pt={16}>
      <List label="potencies" mb={16}>
        {validPotencySliderKeys.includes('cbdPotencies') && (
          <List.Item>
            <SliderField
              name="cbdPotencies"
              endUnit="%"
              {...getFilterOptionsRange('cbdPotencies')}
              label={FilterCategoryLabel['cbdPotencies']}
              disabled={!cbdValidValues}
              defaultValue={cbdDisplayVals}
              value={cbdDisplayVals}
              onChange={changeCbdDisplayVals}
              onAfterChange={onAfterSliderChange('cbdPotencies')}
              isDouble
            />
          </List.Item>
        )}
        {validPotencySliderKeys.includes('thcPotencies') && (
          <List.Item>
            <SliderField
              name="thcPotencies"
              endUnit="%"
              {...getFilterOptionsRange('thcPotencies')}
              label={FilterCategoryLabel['thcPotencies']}
              disabled={!thcValidValues}
              defaultValue={thcDisplayVals}
              value={thcDisplayVals}
              onChange={changeThcDisplayVals}
              onAfterChange={onAfterSliderChange('thcPotencies')}
              isDouble
            />
          </List.Item>
        )}
      </List>
      <FilterActions
        availableProductsCount={availableProductsCount}
        handleClearAll={resetAllPotencies}
        handleClose={closePopover}
        sticky
      />
    </Box>
  );
}
